import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { DefaultRefs, DefaultView } from './default-view';
import { Dropdown, DropdownRefs } from './dropdown';
import { SelectOption, SelectValue } from './select.interface';

type SelectProps = {
  values: SelectValue[];
  label: string;
  displayLabel?: boolean;
  disabled?: boolean;
  value: string;
  defaultValue: string;
  className?: string;
  type: 'primary' | 'secondary';
  onChange: Dispatch<SetStateAction<SelectOption>>;
  openList: boolean;
  setOpenList: (x: boolean) => void;
  searchAll?: () => void;
};

const Select = ({
  values,
  label,
  displayLabel = true,
  disabled = false,
  value,
  defaultValue,
  onChange,
  className,
  type,
  setOpenList,
  openList,
  searchAll,
}: SelectProps) => {
  //Sets the display of the list
  //False - hidden

  //REFS
  const dropdownRefs = useRef<DropdownRefs>(null);
  const defaultViewRefs = useRef<DefaultRefs>(null);
  // const listRef = useRef<HTMLUListElement>(null);
  //const listContainerRef = useRef<HTMLDivElement>(null);
  const dropdownSelectedNodeRef = useRef<HTMLDivElement>(null);

  //Array for the listitem refs
  const listItemsRef = useRef<Array<HTMLLIElement>>([]);

  //Check to see that element exists
  //Add refs to the array
  const addToRef = (el: HTMLLIElement) => {
    if (el && !listItemsRef.current.includes(el)) {
      listItemsRef.current.push(el);
    }
  };
  //-----
  //Options in the select menu
  const [options] = useState<SelectValue[]>(values);

  //KeyCodes for different keypresses
  const SPACEBAR_KEY_CODE = 'Spacebar';
  const ENTER_KEY_CODE = 'Enter';
  const DOWN_ARROW_KEY_CODE = 'ArrowDown';
  const UP_ARROW_KEY_CODE = 'ArrowUp';
  const ESCAPE_KEY_CODE = 'Escape';

  //Sets select value
  //Toggles the menu to false
  const handleValue = (selectedOption: SelectOption) => {
    onChange(selectedOption);
    closeList();
  };

  //KeyDown handler
  //Checks for arrowinput Up, Down - sets new item accordingly
  //Checks for enter - sets item if targeted otherwise closes
  //checks for Escapae - closes the menu
  const handleKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
    const selectedOption: SelectOption = {
      optionText: e.currentTarget.innerText,
      optionValue: e.currentTarget.dataset.optionValue ?? '',
    };

    switch (e.nativeEvent.code) {
      case ENTER_KEY_CODE:
        handleValue(selectedOption);
        closeList();
        return;

      case DOWN_ARROW_KEY_CODE:
        nextItem();
        return;

      case UP_ARROW_KEY_CODE:
        prevItem();
        return;

      case ESCAPE_KEY_CODE:
        closeList();
        return;

      default:
        return;
    }
  };

  //Close list handler
  const closeList = () => {
    const node = dropdownRefs.current?.listContainerRef?.current;
    setOpenList(false);
    if (node) {
      node.setAttribute('aria-expanded', 'false');
    }
  };

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const numberOfItems = listItemsRef.current.length - 1;
  const nextItem = (): void => {
    if (activeIndex < numberOfItems) {
      setActiveIndex(activeIndex + 1);
    } else if (activeIndex === numberOfItems) {
      setActiveIndex(0);
    }
  };
  const prevItem = (): void => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    } else if (activeIndex === 0) {
      setActiveIndex(numberOfItems);
    }
  };

  const isMouseEvent = (
    e: React.MouseEvent | React.KeyboardEvent | React.PointerEvent,
  ): e is React.MouseEvent => {
    return (
      e.nativeEvent instanceof MouseEvent ||
      e.nativeEvent instanceof PointerEvent
    );
  };

  const isKeyboardEvent = (
    e: React.MouseEvent | React.KeyboardEvent | React.PointerEvent,
  ): e is React.KeyboardEvent => {
    return e.nativeEvent instanceof KeyboardEvent;
  };

  useEffect(() => {
    listItemsRef?.current[activeIndex]?.focus();
  }, [activeIndex]);

  //Checks if the menu is open or not
  //handles up & down navigation in the menu with keypressdowns
  function toggleListVisibility(e: React.MouseEvent | React.KeyboardEvent) {
    let openDropDown = false;

    if (isKeyboardEvent(e)) {
      openDropDown =
        SPACEBAR_KEY_CODE.includes(e.nativeEvent.code) ||
        e.nativeEvent.code === ENTER_KEY_CODE;
      if (openDropDown && !disabled) {
        setOpenList(!openList);
      }
      if (e.nativeEvent.code === ESCAPE_KEY_CODE) {
        closeList();
      }
      if (e.nativeEvent.code === DOWN_ARROW_KEY_CODE) {
        nextItem();
      }

      if (e.nativeEvent.code === UP_ARROW_KEY_CODE) {
        prevItem();
      }
    }
    if (isMouseEvent(e)) {
      if ((e.type === 'click' || openDropDown) && !disabled) {
        setOpenList(!openList);
        const node = dropdownRefs.current?.listContainerRef?.current;
        if (node && !disabled) {
          node.setAttribute('aria-expanded', 'true');
        }
      }
    }
  }

  useEffect(() => {
    if (openList && type === 'primary') {
      dropdownSelectedNodeRef.current?.setAttribute(
        'class',
        `relative w-auto border bg-background-primary flex justify-center items-start flex-col py-2.5 ${className} rounded-full`,
      );
      return;
    }
    if (!openList && type === 'primary') {
      dropdownSelectedNodeRef.current?.setAttribute(
        'class',
        `focus-within:outline focus-within:outline-offset-3 focus-within:outline-3 focus-within:outline-text-light relative w-auto border bg-background-primary flex justify-center items-start flex-col py-2.5 ${className} rounded-full`,
      );
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openList]);

  const wrapperClasses =
    (className && type === 'primary') ||
    (className && !openList && type === 'secondary')
      ? `w-auto flex-auto border-brand-grey-2 ${className} rounded-full`
      : (!className && type === 'primary') ||
          (!openList && type === 'secondary')
        ? 'flex w-auto rounded-full'
        : className && openList && type === 'secondary'
          ? `flex h-auto w-auto border-brand-grey-2 ${className} rounded-xl`
          : '';

  const dropdownId = useId();

  return (
    <>
      <small
        id={dropdownId}
        className={`font-sans text-bodys font-normal leading-[2.5]${
          disabled ? ' text-disabled-light' : ' text-icon-default'
        }${!displayLabel ? ' hidden' : ''}`}
      >
        {label}
      </small>

      <div
        className={`relative flex-col items-start justify-center border bg-background-primary py-2.5 focus-within:outline focus-within:outline-3 focus-within:outline-offset-3 focus-within:outline-text-light ${wrapperClasses}`}
        role="button"
        aria-labelledby={dropdownId}
        tabIndex={0}
        ref={dropdownSelectedNodeRef}
        onClick={toggleListVisibility}
        onKeyDown={toggleListVisibility}
      >
        <div
          className={`flex h-auto w-full items-center justify-between pr-6 pl-4${
            type === 'secondary' && openList
              ? ' w-full flex-col overflow-hidden'
              : ''
          }`}
        >
          {type === 'secondary' && (
            <div className="flex h-full w-full items-center justify-between">
              <DefaultView
                value={value}
                ref={defaultViewRefs}
                openList={openList}
                defaultValue={defaultValue}
                disabled={disabled}
              />
            </div>
          )}

          {type === 'primary' && (
            <DefaultView
              value={value}
              ref={defaultViewRefs}
              openList={openList}
              defaultValue={defaultValue}
              disabled={disabled}
            />
          )}

          {type === 'secondary' && !disabled ? (
            <Dropdown
              ref={dropdownRefs}
              addToRef={addToRef}
              openList={openList}
              type={type}
              defaultValue={defaultValue}
              options={options}
              handleValue={handleValue}
              handleKeyDown={handleKeyDown}
              className={className}
              searchAll={searchAll}
              setOpenList={setOpenList}
            />
          ) : null}
        </div>
        {type === 'primary' && !disabled ? (
          <Dropdown
            ref={dropdownRefs}
            addToRef={addToRef}
            openList={openList}
            type={type}
            defaultValue={defaultValue}
            options={options}
            handleValue={handleValue}
            handleKeyDown={handleKeyDown}
            className={className}
            searchAll={searchAll}
            setOpenList={setOpenList}
          />
        ) : null}
      </div>
    </>
  );
};

export default Select;
